import { clsx } from 'clsx'
import { useEffect, useId, useRef, useState, type FC } from 'react'

import { Button } from '@/components/core/Button'
import { CheckIcon, CloseIcon } from '@/components/core/Icon/icons'
import { lockBodyScroll, unlockBodyScroll } from '@/lib/bodyScrollLock'
import type { ConsentCategories } from '@/lib/cookieConsent'
import { EXTERNAL_URLS, ROUTES } from '@/routes/paths'

import './index.scss'

export type CookieBannerView = 'summary' | 'preferences'

export interface CookieBannerProps {
  open: boolean
  categories: ConsentCategories
  setCategory: (key: keyof ConsentCategories, value: boolean) => void
  onAcceptAll: () => void
  onDenyAll: () => void
  onSavePreferences: () => void
  onDismiss: () => void
  /** Face to open on. Defaults to the summary. */
  initialView?: CookieBannerView
}

/**
 * Cookie-consent dialog (Figma "Login - Chat GPT - Cookies"). Summary face: copy + Deny /
 * Change preferences / Accept all. Preferences face: category rows + Deny / Save my choice /
 * Accept all. A bottom sheet at the mobile breakpoint, in CSS only. Presentational —
 * `useCookieConsentBanner` owns the state.
 */
export const CookieBanner: FC<CookieBannerProps> = ({ open, ...dialogProps }) => {
  // Unmounting on close resets `view` for the next open.
  if (!open) return null

  return <CookieBannerDialog {...dialogProps} />
}

const CookieBannerDialog: FC<Omit<CookieBannerProps, 'open'>> = ({
  categories,
  setCategory,
  onAcceptAll,
  onDenyAll,
  onSavePreferences,
  onDismiss,
  initialView = 'summary',
}) => {
  const [view, setView] = useState<CookieBannerView>(initialView)
  const dialogRef = useRef<HTMLDivElement>(null)
  const mouseDownStartedOnOverlayRef = useRef(false)
  // Ladle renders a story four times in one document; ids must not collide.
  const titleId = useId()

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onDismiss()
      }
    }

    document.addEventListener('keydown', handleEscape)
    // The shared ref-counted lock, not `body.style.overflow` directly: this dialog can
    // open from inside the mobile nav drawer, which holds the same lock.
    lockBodyScroll()
    dialogRef.current?.focus()

    return () => {
      document.removeEventListener('keydown', handleEscape)
      unlockBodyScroll()
    }
  }, [onDismiss])

  const isPreferences = view === 'preferences'

  return (
    <div
      className="cookie-banner"
      data-testid="cookie-banner"
      onMouseDown={e => {
        mouseDownStartedOnOverlayRef.current = e.target === e.currentTarget
      }}
      onClick={e => {
        // A drag that starts inside the dialog is a text selection, not a dismissal.
        const shouldClose = e.target === e.currentTarget && mouseDownStartedOnOverlayRef.current
        mouseDownStartedOnOverlayRef.current = false
        if (shouldClose) onDismiss()
      }}
    >
      <div
        ref={dialogRef}
        className="cookie-banner__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <button
          type="button"
          className="cookie-banner__close"
          aria-label="Close cookie settings"
          data-testid="cookie-banner-close"
          onClick={onDismiss}
        >
          <CloseIcon />
        </button>

        <div className={clsx('cookie-banner__content', isPreferences && 'cookie-banner__content--preferences')}>
          <h2 id={titleId} className="cookie-banner__title">
            Cookie Settings
          </h2>

          {isPreferences ? (
            <div className="cookie-banner__categories">
              <ConsentCategoryRow
                name="Necessary"
                description="Required for the site to function – this includes accessibility and security. Always on."
                checked
                disabled
              />
              <ConsentCategoryRow
                name="Analytics"
                description="Helps us understand how visitors use our site so we can improve it."
                checked={categories.analytics}
                onChange={checked => setCategory('analytics', checked)}
                data-testid="cookie-analytics-checkbox"
              />
            </div>
          ) : (
            <p className="cookie-banner__body">
              We use essential cookies to run your practice, and optional ones to improve it. Read more in our{' '}
              <a href={EXTERNAL_URLS.PRIVACY_POLICY} target="_blank" rel="noreferrer" className="cookie-banner__link">
                Privacy Notice
              </a>{' '}
              and{' '}
              <a
                href={ROUTES.TERMS_OF_SERVICE.fullPath}
                target="_blank"
                rel="noreferrer"
                className="cookie-banner__link"
              >
                Terms of Service
              </a>
              .
            </p>
          )}
        </div>

        <div className="cookie-banner__buttons">
          {/* One row on the mobile sheet; `display: contents` on desktop. */}
          <div className="cookie-banner__secondary-buttons">
            <Button
              variant="secondary"
              size="small"
              className="cookie-banner__button"
              onClick={onDenyAll}
              data-testid="cookie-banner-deny"
            >
              Deny
            </Button>
            {/* One element for both faces, so focus survives the swap. */}
            <Button
              variant="outline"
              size="small"
              className="cookie-banner__button"
              onClick={isPreferences ? onSavePreferences : () => setView('preferences')}
              data-testid={isPreferences ? 'cookie-banner-save' : 'cookie-banner-change-preferences'}
            >
              {isPreferences ? 'Save my choice' : 'Change preferences'}
            </Button>
          </div>
          <Button
            variant="primary"
            size="small"
            className="cookie-banner__button"
            onClick={onAcceptAll}
            data-testid="cookie-banner-accept"
          >
            Accept all
          </Button>
        </div>
      </div>
    </div>
  )
}

interface ConsentCategoryRowProps {
  name: string
  description: string
  checked: boolean
  /** Always on, muted fill, not interactive. */
  disabled?: boolean
  onChange?: (checked: boolean) => void
  'data-testid'?: string
}

// Hidden native checkbox for semantics and keyboard; the drawn box is what the design shows.
const ConsentCategoryRow: FC<ConsentCategoryRowProps> = ({
  name,
  description,
  checked,
  disabled = false,
  onChange,
  'data-testid': testId,
}) => (
  <label className={clsx('cookie-banner__category', disabled && 'cookie-banner__category--locked')}>
    <span className="cookie-banner__category-header">
      <input
        type="checkbox"
        className="cookie-banner__checkbox-input"
        checked={checked}
        disabled={disabled}
        readOnly={onChange === undefined}
        onChange={onChange === undefined ? undefined : e => onChange(e.target.checked)}
        data-testid={testId}
      />
      <span className="cookie-banner__checkbox" aria-hidden="true">
        <CheckIcon size={20} />
      </span>
      <span className="cookie-banner__category-name">{name}</span>
    </span>
    <span className="cookie-banner__category-desc">{description}</span>
  </label>
)
